import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import api from '../axios_Interceptor/api';
import { SERVER_ADDRESS } from '../server';
import MemberSignupForm, { MemberFields, MemberSubmission } from '../../Page/User/Sign/MemberSignupForm';
import UserModalHeader from '../UI/UserModalHeader';

type Result = 'ready' | 'updated' | false;
type Purpose = 'nickname' | 'purchase';
type Profile = MemberFields & { complete: boolean; terms_agree: string; privacy_agree: string };
const endpoint = `${SERVER_ADDRESS}user/profile/details`;
const MemberDetails = createContext<{ ensureDetails: (purpose?: Purpose) => Promise<Result> }>({
  ensureDetails: async () => { throw new Error('회원정보 확인 화면을 불러오지 못했습니다. 새로고침해주세요.'); },
});
export const useMemberDetails = () => useContext(MemberDetails);

export function MemberDetailsProvider({ children }: { children: ReactNode }) {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [purpose, setPurpose] = useState<Purpose>('purchase');
  const pending = useRef<Promise<Result> | null>(null);
  const resolve = useRef<((result: Result) => void) | null>(null);
  const generation = useRef(0);
  const dialog = useRef<HTMLDialogElement>(null);
  const { pathname } = useLocation();
  const finish = useCallback((result: Result) => {
    generation.current += 1;
    setProfile(null);
    resolve.current?.(result);
    resolve.current = null;
  }, []);

  useEffect(() => () => {
    generation.current += 1;
    resolve.current?.(false);
    resolve.current = null;
    pending.current = null;
    setProfile(null);
  }, [pathname]);

  useEffect(() => {
    if (profile) dialog.current?.showModal();
  }, [profile]);

  const ensureDetails = useCallback((reason: Purpose = 'purchase') => {
    if (pending.current) return pending.current;
    const request = generation.current;
    const operation = (async (): Promise<Result> => {
      const { data } = await api.get<Profile>(endpoint);
      if (request !== generation.current) return false;
      if (reason === 'nickname' ? !!data.nickname?.trim() : data.complete === true) return 'ready';
      return new Promise<Result>(done => { resolve.current = done; setPurpose(reason); setProfile(data); });
    })().finally(() => { if (pending.current === operation) pending.current = null; });
    pending.current = operation;
    return operation;
  }, []);

  const save = async (values: MemberSubmission) => {
    const request = generation.current;
    const { data } = await api.post<Profile>(endpoint, values);
    if (request !== generation.current) return;
    if (!data.complete) throw new Error('필수 정보를 확인해주세요.');
    finish('updated');
  };

  return <MemberDetails.Provider value={{ ensureDetails }}>
    {children}
    {profile && <Dialog ref={dialog} aria-label={purpose === 'nickname' ? '닉네임 입력' : '상세 정보 입력'} onCancel={event => { event.preventDefault(); finish(false); }}>
      <UserModalHeader title={purpose === 'nickname' ? '닉네임 입력' : '상세 정보 입력'} closeLabel="닫기" onClose={() => finish(false)} />
      <DialogBody>
        {purpose === 'nickname' ? <NicknameForm onSaved={() => finish('updated')} />
          : <MemberSignupForm details ready initialValues={profile} onSubmit={save}
            initialTerms={profile.terms_agree?.toLowerCase() === 'y'} initialPrivacy={profile.privacy_agree?.toLowerCase() === 'y'} />}
      </DialogBody>
    </Dialog>}
  </MemberDetails.Provider>;
}

function NicknameForm({ onSaved }: { onSaved: () => void }) {
  const [nickname, setNickname] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const active = useRef(true);
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  return <NicknameFields onSubmit={async event => {
    event.preventDefault();
    if (saving) return;
    setSaving(true); setError('');
    try {
      await api.patch(`${SERVER_ADDRESS}user/profile`, { nickname: nickname.trim() });
      if (active.current) onSaved();
    } catch (cause: any) {
      if (active.current) setError(cause?.response?.data?.message || '닉네임을 저장하지 못했습니다. 다시 시도해주세요.');
    } finally { if (active.current) setSaving(false); }
  }}>
    <p>채팅과 문의에 사용할 닉네임을 입력해주세요.</p>
    <label htmlFor="member-nickname">닉네임</label>
    <input id="member-nickname" autoFocus required pattern=".*\S.*" maxLength={10}
      value={nickname} onChange={event => setNickname(event.target.value)} disabled={saving} />
    <p>닉네임은 1~10자로 입력해주세요.</p>
    {error && <p role="alert">{error}</p>}
    <button type="submit" disabled={saving || !nickname.trim() || Array.from(nickname.trim()).length > 10}>{saving ? '저장 중…' : '저장하기'}</button>
  </NicknameFields>;
}

const NicknameFields = styled.form`
  padding: 20px 24px 24px; p { font-size: 14px; line-height: 1.5; color: var(--user-text-muted); }
  p:first-child { margin-top: 0; }
  label { display: block; margin-bottom: 8px; }
  input, button { width: 100%; box-sizing: border-box; min-height: 44px; padding: 10px; font: inherit;
    border: 1px solid var(--user-border); border-radius: 8px; color: var(--user-text); background: var(--user-surface); }
  button { color: white; background: var(--user-button); cursor: pointer; }
  button:disabled { opacity: .5; cursor: default; }
  input:focus-visible, button:focus-visible { outline: 2px solid var(--user-accent); outline-offset: 2px; }
`;

const Dialog = styled.dialog`
  width: min(100% - 24px, 500px); max-height: 90dvh; overflow: hidden; padding: 0;
  border: 1px solid var(--user-border); border-radius: 12px; box-sizing: border-box;
  color: var(--user-text); background: var(--user-surface);
  font-family: var(--font-Pretendard-Regular);
  &[open] { display: flex; flex-direction: column; }
  &::backdrop { background: rgba(0, 0, 0, .55); }
`;
const DialogBody = styled.div`
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
`;
